import React, { useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { graphql, Link } from 'gatsby';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

import { useBusinessSolutionsData } from './hooks';
import { BUSINESS_SOLUTIONS_VARIABLES } from './constants';

import HorizontalArrow from '../assets/horizontalArrow.inline.svg';

import { RichText } from 'components/Prismic';

interface ISolutionsPanels {
  solutions: ReturnType<typeof useBusinessSolutionsData>['solutions'];
  activeIndex: number;
}

enum SOLUTIONS_PANELS_VARIABLES {
  panelMargin = '--panel-margin',
}

const buildPanelClass = (index: number) => `panel-${index + 1}`;

const ANIMATION_DURATION = 3;
const ease = CustomEase.create(
  'custom',
  'M0,0 C0.264,0 0.275,0.057 0.3,0.1 0.344,0.176 0.368,0.29 0.4,0.5 0.434,0.728 0.53,0.811 0.59,0.86 0.636,0.898 0.818,0.986 1,1 '
);

const Container = styled.div`
  display: grid;
`;

const Panel = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    grid-template-rows: repeat(3, min-content);
    grid-row: 1 / span 1;
    grid-column: 1 / -1;
    align-content: center;
    opacity: 0;
    border-radius: 2rem;
    background-color: var(${BUSINESS_SOLUTIONS_VARIABLES.foregroundColor});
    padding: 6rem 5rem;

    ${mq.md} {
      ${SOLUTIONS_PANELS_VARIABLES.panelMargin}: -100%;

      align-content: initial;
      margin-bottom: var(${SOLUTIONS_PANELS_VARIABLES.panelMargin});
      background-color: transparent;
      padding: 1.5rem 2.5rem 2rem;
      overflow: hidden;

      &::after {
        display: block;
        grid-row: 2 / span 2;
        grid-column: 1 / -1;
        z-index: -1;
        margin-top: -2.75rem;
        margin-left: -2.5rem;
        border-radius: inherit;
        background-color: var(${BUSINESS_SOLUTIONS_VARIABLES.foregroundColor});
        width: calc(100% + 5rem);
        height: calc(100% + 4.75rem);
        content: '';
        pointer-events: none;
      }
    }
  `}
`;

const Title = styled.h3`
  ${({ theme: { mq, tp } }) => css`
    grid-row: 1 / span 1;
    grid-column: 1 / -1;
    margin: 0 0 1.5rem;
    line-height: 1.75;
    color: var(${BUSINESS_SOLUTIONS_VARIABLES.primaryTextColor});
    font-size: 3.75rem;

    ${tp.useFont(fonts => fonts.Switzer.semiBold)};

    ${mq.md} {
      margin-bottom: 4.5rem;
      line-height: 1.29;
      font-size: 3rem;

      ${tp.useFont(fonts => fonts.Switzer.regular)};
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    flex-direction: column;
    grid-row: 2 / span 1;
    grid-column: 1 / -1;

    & > p {
      line-height: 1.5;
      color: var(${BUSINESS_SOLUTIONS_VARIABLES.tertiaryTextColor});
      font-size: 2.75rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      ${mq.md} {
        line-height: 1.4;
        font-size: 1.75rem;
      }
    }
  `}
`;

const arrowAnimation = keyframes`
  0% {
    transform: translateX(0);  
  }
  
  100% {
    transform: translateX(1.25rem) ;
  }

`;

const Button = styled(Link)`
  ${({ theme: { colors, durations, mq, tp } }) => css`
    display: flex;
    grid-row: 3 / span 1;
    grid-column: 1 / -1;
    column-gap: 1rem;
    align-items: center;
    justify-self: flex-start;
    margin-top: 4rem;
    border-radius: 2rem;
    background-color: ${colors.primary};
    padding: 2.1875rem 4.6875rem;

    ${tp.useFont(fonts => fonts.Switzer.semiBold)};

    text-decoration: none;
    line-height: 1.39;
    color: ${colors.black}99;
    font-size: 2.25rem;

    & > svg {
      transition: transform ${durations.long} ease-in-out;
      width: 4rem;
      height: 4rem;
    }

    &:hover {
      & > svg {
        transform: translateX(1.5rem);
        transition: transform ${durations.long} ease-in-out;
      }
    }

    ${mq.md} {
      justify-content: center;
      justify-self: stretch;
      padding: 1.5rem 0;
      line-height: 1.375;
      font-size: 2rem;

      & > svg {
        animation: ${arrowAnimation} 2s ease-in-out infinite alternate;

        &:hover {
          transform: initial;
        }
      }
    }
  `}
`;

const SolutionsPanels: React.FC<ISolutionsPanels> = ({ solutions, activeIndex }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const activePanelElement = containerRef.current?.querySelector(
        `.${buildPanelClass(activeIndex)}`
      );
      gsap.fromTo(
        activePanelElement,
        {
          yPercent: 100,
          opacity: 0,
          zIndex: 2,
          [SOLUTIONS_PANELS_VARIABLES.panelMargin]: '0',
        },
        {
          yPercent: 0,
          ease,
          opacity: 1,
          zIndex: 2,
          [SOLUTIONS_PANELS_VARIABLES.panelMargin]: '0',
          duration: ANIMATION_DURATION,
        }
      );
    }

    return () => {
      if (containerRef.current) {
        const previousPanelElement = containerRef.current?.querySelector(
          `.${buildPanelClass(activeIndex)}`
        );
        gsap.to(previousPanelElement, {
          opacity: 0,
          zIndex: 0,
          [SOLUTIONS_PANELS_VARIABLES.panelMargin]: '-100%',
          ease: 'expo.out',
          duration: ANIMATION_DURATION,
        });
      }
    };
  }, [activeIndex]);

  return (
    <Container ref={containerRef}>
      {solutions.map(({ title, description, link, linkLabel }, index) => (
        <Panel key={title} className={buildPanelClass(index)}>
          <Title>{title}</Title>
          <Description render={description} />
          <Button to={link}>
            {linkLabel}
            <HorizontalArrow />
          </Button>
        </Panel>
      ))}
    </Container>
  );
};

export const contentBusinessSolutionsSolutionsPanelsFragment = graphql`
  fragment HomeContentBusinessSolutionsSolutionsPanelsData on PrismicHomeDataContentHomebusinesssolutionsItem {
    home_business_solutions_panel_title
    home_business_solutions_panel_description {
      richText
    }
    home_business_solutions_panel_link {
      url
    }
    home_business_solutions_panel_link_label
  }
`;

export default SolutionsPanels;
